/**
 * API Client Service
 *
 * Modern fetch-based HTTP client replacing XMLHttpRequest usage.
 * Provides both async and sync (for legacy compatibility) methods.
 */

/** API response wrapper */
export interface ApiResponse<T = unknown> {
    ok: boolean;
    status: number;
    statusText: string;
    data: T;
    headers: Headers;
}

/** API error with response details */
export class ApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly statusText: string,
        public readonly responseText: string,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

/** Request configuration options */
export interface RequestOptions {
    headers?: Record<string, string>;
    timeout?: number;
}

/**
 * API Client for making HTTP requests.
 *
 * Replaces the legacy XMLHttpRequest-based approach with modern fetch API.
 */
export class ApiClient {
    private baseUrl: string;
    private defaultTimeout: number;
    private defaultHeaders: Record<string, string>;

    constructor(options?: {
        baseUrl?: string;
        timeout?: number;
        headers?: Record<string, string>;
    }) {
        this.baseUrl = options?.baseUrl ?? '';
        this.defaultTimeout = options?.timeout ?? 30000;
        this.defaultHeaders = {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...options?.headers,
        };
    }

    /**
     * Make a GET request.
     *
     * @param url - The URL to request
     * @param options - Optional request configuration
     * @returns Promise resolving to the response
     */
    async get<T = string>(
        url: string,
        options?: RequestOptions,
    ): Promise<ApiResponse<T>> {
        return this.request<T>('GET', url, undefined, options);
    }

    /**
     * Make a POST request.
     *
     * @param url - The URL to request
     * @param data - The data to send (will be URL-encoded)
     * @param options - Optional request configuration
     * @returns Promise resolving to the response
     */
    async post<T = string>(
        url: string,
        data?: string | Record<string, string>,
        options?: RequestOptions,
    ): Promise<ApiResponse<T>> {
        const body = typeof data === 'object' ? this.encodeParams(data) : data;
        return this.request<T>('POST', url, body, options);
    }

    /**
     * Make a synchronous GET request (for legacy compatibility).
     *
     * @deprecated Prefer async get() method. This is for backward compatibility only.
     * @param url - The URL to request
     * @returns The response text
     */
    getSync(url: string): string {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', this.baseUrl + url, false); // false = synchronous
        xhr.send();

        if (xhr.status >= 200 && xhr.status < 300) {
            return xhr.responseText;
        }

        throw new ApiError(
            `Request failed: ${xhr.status} ${xhr.statusText}`,
            xhr.status,
            xhr.statusText,
            xhr.responseText,
        );
    }

    /**
     * Make a synchronous POST request (for legacy compatibility).
     *
     * @deprecated Prefer async post() method. This is for backward compatibility only.
     * @param url - The URL to request
     * @param data - The data to send
     * @returns The response text
     */
    postSync(url: string, data?: string): string {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', this.baseUrl + url, false); // false = synchronous
        xhr.setRequestHeader(
            'Content-Type',
            'application/x-www-form-urlencoded',
        );
        xhr.send(data);

        if (xhr.status >= 200 && xhr.status < 300) {
            return xhr.responseText;
        }

        throw new ApiError(
            `Request failed: ${xhr.status} ${xhr.statusText}`,
            xhr.status,
            xhr.statusText,
            xhr.responseText,
        );
    }

    /**
     * Internal method to make a request.
     */
    private async request<T>(
        method: string,
        url: string,
        body?: string,
        options?: RequestOptions,
    ): Promise<ApiResponse<T>> {
        const controller = new AbortController();
        const timeout = options?.timeout ?? this.defaultTimeout;

        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(this.baseUrl + url, {
                method,
                headers: {
                    ...this.defaultHeaders,
                    ...options?.headers,
                },
                body,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            const text = await response.text();

            // Try to parse as JSON, fall back to text
            let data: T;
            try {
                data = JSON.parse(text) as T;
            } catch {
                data = text as unknown as T;
            }

            return {
                ok: response.ok,
                status: response.status,
                statusText: response.statusText,
                data,
                headers: response.headers,
            };
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof Error && error.name === 'AbortError') {
                throw new ApiError('Request timeout', 0, 'Timeout', '');
            }

            throw error;
        }
    }

    /**
     * URL-encode an object of parameters.
     */
    private encodeParams(params: Record<string, string>): string {
        return Object.entries(params)
            .map(
                ([key, value]) =>
                    `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
            )
            .join('&');
    }
}

/** Default API client instance */
export const apiClient = new ApiClient();
